import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { AuthenticationService } from '../services/authentication.service';
import { SharedService } from '../../services/shared.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-sign-in',
    imports: [MatFormFieldModule, MatInputModule, MatButtonModule, MatCheckboxModule, ReactiveFormsModule, NgIf],
    templateUrl: './sign-in.component.html',
    styleUrl: './sign-in.component.scss'
})
export class SignInComponent implements OnInit {

    isToggled = false;
    allowedGroups: string[] = ['admin'];

    constructor(
        private fb: FormBuilder,
        private router: Router,
        public themeService: CustomizerSettingsService,
        private _service: AuthenticationService,
        private sharedService: SharedService,
        private toastr: ToastrService
    ) {
        this.authForm = this.fb.group({
            email: ['', [Validators.required]],
            password: ['', [Validators.required, Validators.minLength(8)]],
            remember_me: [false]
        });
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
    }

    ngOnInit(): void {
        const token = localStorage.getItem('token');
        const user  = localStorage.getItem('user');

        if (token && user) {
            // Valid session found — verify with API before allowing entry
            this.verifyExistingSession(token);
        }
        // No token/user — just show the login form, do NOT touch localStorage here.
        // Clearing is logout()'s responsibility, not the login page's.
    }

    /**
     * Verifies the stored token against the API.
     * Success → refresh stored session and go to dashboard.
     * Error   → token is invalid/expired; clear session keys only and stay on login.
     */
    private verifyExistingSession(token: string): void {
        const refresh_token = localStorage.getItem('refresh_token') ?? null;

        this.sharedService.postRequest('/user/verify_token', { token, refresh_token })
            .subscribe({
                next: (res: any) => {
                    // Overwrite with fresh token/user returned by the API
                    localStorage.setItem('user',  JSON.stringify(res));
                    localStorage.setItem('token', res.token);

                    if (res.refresh_token) {
                        localStorage.setItem('refresh_token', res.refresh_token);
                        localStorage.setItem('refresh_token_issued_at', Date.now().toString());
                    }

                    this.router.navigate(['/dashboard']);
                },
                error: () => {
                    // Only remove session keys — do not blindly clear() everything
                    localStorage.removeItem('user');
                    localStorage.removeItem('token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('refresh_token_issued_at');
                    this.toastr.warning('Your session has expired. Please sign in again.');
                }
            });
    }

    // Password Hide
    hide = true;

    // Form
    authForm: FormGroup;

    onSubmit() {
        if (this.authForm.valid) {
            const body = this.authForm.getRawValue();

            this._service.login(body).subscribe(
                (res: any) => {
                    // Clear previous session keys before writing new ones
                    localStorage.removeItem('user');
                    localStorage.removeItem('token');

                    localStorage.setItem('user', JSON.stringify(res));
                    localStorage.setItem('token', res.token);
                    localStorage.setItem('hasReloaded', 'false');

                    if (body.remember_me) {
                        localStorage.setItem('refresh_token', res.refresh_token);
                        localStorage.setItem('refresh_token_issued_at', Date.now().toString());
                        localStorage.setItem('rememberMe', 'true');
                    } else {
                        this.clearRefreshToken();
                    }

                    this.router.navigate(['/dashboard']);
                },
                (err) => {
                    this.toastr.error(err.error.message);
                }
            );
        }
    }

    clearRefreshToken() {
        // localStorage.removeItem('refresh_token');
        // localStorage.removeItem('refresh_token_issued_at');
        // localStorage.removeItem('rememberMe');
    }

    // Dark Mode
    toggleTheme() {
        this.themeService.toggleTheme();
    }

    // Card Border
    toggleCardBorderTheme() {
        this.themeService.toggleCardBorderTheme();
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    forgotPass() {
        this.toastr.info('Contact your IT Department for Password Reset');
    }
}